import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listTeam,
  assignRoleByEmail,
  removeRole,
  createStaffUser,
  updateStaffUser,
  deleteStaffUser,
} from "@/lib/team.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2,
  Trash2,
  UserPlus,
  ShieldCheck,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/team")({
  component: TeamPage,
});

type Role = "owner" | "manager" | "host" | "door";

const ROLE_META: Record<string, { label: string; color: string; desc: string }> = {
  owner: {
    label: "Owner",
    color: "bg-amber-500/15 text-amber-300 border-amber-400/30",
    desc: "Full control",
  },
  manager: {
    label: "Manager",
    color: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
    desc: "Floor + operations",
  },
  host: {
    label: "Host",
    color: "bg-sky-500/15 text-sky-300 border-sky-400/30",
    desc: "Reservations + guests",
  },
  door: {
    label: "Door",
    color: "bg-violet-500/15 text-violet-300 border-violet-400/30",
    desc: "Check-in only",
  },
};

type Member = {
  user_id: string;
  email: string | null;
  full_name: string | null;
  roles: { id: string; role: string; created_at: string }[];
};

function TeamPage() {
  const listFn = useServerFn(listTeam);
  const assignFn = useServerFn(assignRoleByEmail);
  const removeFn = useServerFn(removeRole);
  const createFn = useServerFn(createStaffUser);
  const updateFn = useServerFn(updateStaffUser);
  const deleteFn = useServerFn(deleteStaffUser);

  const { data, isLoading, refetch, error } = useQuery({
    queryKey: ["admin-team"],
    queryFn: () => listFn(),
  });

  // Create form
  const [cEmail, setCEmail] = useState("");
  const [cName, setCName] = useState("");
  const [cPass, setCPass] = useState("");
  const [cRole, setCRole] = useState<Role>("host");
  const [cBusy, setCBusy] = useState(false);

  // Grant form
  const [gEmail, setGEmail] = useState("");
  const [gRole, setGRole] = useState<Role>("host");
  const [gBusy, setGBusy] = useState(false);

  // Edit dialog
  const [editing, setEditing] = useState<Member | null>(null);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCBusy(true);
    try {
      await createFn({
        data: {
          email: cEmail.trim(),
          password: cPass,
          full_name: cName.trim(),
          role: cRole,
        },
      });
      toast.success(`${ROLE_META[cRole].label} created for ${cEmail}`);
      setCEmail("");
      setCName("");
      setCPass("");
      await refetch();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setCBusy(false);
    }
  }

  async function handleAssign(e: React.FormEvent) {
    e.preventDefault();
    if (!gEmail.trim()) return;
    setGBusy(true);
    try {
      await assignFn({ data: { email: gEmail.trim(), role: gRole } });
      toast.success(`${ROLE_META[gRole].label} role granted to ${gEmail}`);
      setGEmail("");
      await refetch();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setGBusy(false);
    }
  }

  async function handleRemove(roleId: string, label: string) {
    if (!confirm(`Remove ${label} role?`)) return;
    try {
      await removeFn({ data: { role_id: roleId } });
      toast.success("Role removed");
      await refetch();
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function handleDeleteUser(m: Member) {
    if (
      !confirm(
        `Delete ${m.email ?? m.user_id} entirely? This removes the account and all roles.`,
      )
    )
      return;
    try {
      await deleteFn({ data: { user_id: m.user_id } });
      toast.success("User deleted");
      await refetch();
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl">Team & roles</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Create new staff, grant roles to existing accounts, or edit any staff member.
        </p>
      </div>

      {/* Create new staff */}
      <form
        onSubmit={handleCreate}
        className="rounded-2xl border border-border bg-card/40 p-6"
      >
        <div className="mb-4 flex items-center gap-2 text-sm font-medium">
          <UserPlus size={16} className="text-primary" />
          Create new staff account
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label htmlFor="c-name" className="text-xs">Full name</Label>
            <Input
              id="c-name"
              required
              value={cName}
              onChange={(e) => setCName(e.target.value)}
              className="mt-1"
              placeholder="Jane Doe"
            />
          </div>
          <div>
            <Label htmlFor="c-email" className="text-xs">Email</Label>
            <Input
              id="c-email"
              type="email"
              required
              value={cEmail}
              onChange={(e) => setCEmail(e.target.value)}
              className="mt-1"
              placeholder="staff@plunkets.ie"
            />
          </div>
          <div>
            <Label htmlFor="c-pass" className="text-xs">Password (min 8)</Label>
            <Input
              id="c-pass"
              type="text"
              required
              minLength={8}
              value={cPass}
              onChange={(e) => setCPass(e.target.value)}
              className="mt-1"
              placeholder="Set a password"
            />
          </div>
          <div>
            <Label className="text-xs">Role</Label>
            <Select value={cRole} onValueChange={(v) => setCRole(v as Role)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="owner">Owner</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="host">Host</SelectItem>
                <SelectItem value="door">Door</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button type="submit" disabled={cBusy}>
            {cBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create staff"}
          </Button>
        </div>
      </form>

      {/* Grant role to existing */}
      <form
        onSubmit={handleAssign}
        className="rounded-2xl border border-border bg-card/40 p-6"
      >
        <div className="mb-4 flex items-center gap-2 text-sm font-medium">
          <ShieldCheck size={16} className="text-primary" />
          Grant role by email (existing account or invite)
        </div>
        <div className="grid gap-4 md:grid-cols-[1fr_180px_auto]">
          <div>
            <Label htmlFor="t-email" className="text-xs">Email</Label>
            <Input
              id="t-email"
              type="email"
              required
              placeholder="staff@plunkets.ie"
              value={gEmail}
              onChange={(e) => setGEmail(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Role</Label>
            <Select value={gRole} onValueChange={(v) => setGRole(v as Role)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="owner">Owner</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="host">Host</SelectItem>
                <SelectItem value="door">Door</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button type="submit" disabled={gBusy} className="w-full md:w-auto">
              {gBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Grant role"}
            </Button>
          </div>
        </div>
      </form>

      {/* Members list */}
      <div className="rounded-2xl border border-border bg-card/40">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-2 text-sm font-medium">
            <ShieldCheck size={16} className="text-primary" />
            Current staff
          </div>
          <span className="text-xs text-muted-foreground">
            {data?.members.length ?? 0} members
          </span>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center p-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : error ? (
          <div className="p-6 text-sm text-destructive">
            {(error as Error).message}
          </div>
        ) : !data?.members.length ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            No staff yet. Create the first account above.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {data.members.map((m) => (
              <li
                key={m.user_id}
                className="flex flex-col gap-3 px-6 py-4 md:flex-row md:items-center md:justify-between"
              >
                <div className="min-w-0">
                  <div className="font-medium">
                    {m.full_name || m.email || m.user_id.slice(0, 8)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {m.email ?? "—"}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {m.roles.map((r) => {
                    const meta = ROLE_META[r.role] ?? {
                      label: r.role,
                      color: "border-border bg-muted/30",
                      desc: "",
                    };
                    const isOwner = r.role === "owner";
                    return (
                      <span
                        key={r.id}
                        className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs ${meta.color}`}
                      >
                        {meta.label}
                        {!isOwner && (
                          <button
                            type="button"
                            onClick={() => handleRemove(r.id, meta.label)}
                            className="ml-1 opacity-70 hover:opacity-100"
                            aria-label={`Remove ${meta.label}`}
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </span>
                    );
                  })}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing(m)}
                  >
                    <Pencil size={12} className="mr-1" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDeleteUser(m)}
                  >
                    <Trash2 size={12} className="mr-1" /> Delete
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <EditDialog
        member={editing}
        onClose={() => setEditing(null)}
        onSaved={async () => {
          setEditing(null);
          await refetch();
        }}
        updateFn={updateFn}
      />
    </div>
  );
}

function EditDialog({
  member,
  onClose,
  onSaved,
  updateFn,
}: {
  member: Member | null;
  onClose: () => void;
  onSaved: () => void;
  updateFn: (args: {
    data: {
      user_id: string;
      full_name?: string;
      email?: string;
      password?: string;
      add_role?: Role;
    };
  }) => Promise<unknown>;
}) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [addRole, setAddRole] = useState<Role | "">("");
  const [busy, setBusy] = useState(false);

  const open = !!member;

  useEffect(() => {
    if (member) {
      setFullName(member.full_name ?? "");
      setEmail(member.email ?? "");
      setPassword("");
      setAddRole("");
    }
  }, [member]);


  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!member) return;
    setBusy(true);
    try {
      await updateFn({
        data: {
          user_id: member.user_id,
          full_name: fullName || undefined,
          email: email || undefined,
          password: password || undefined,
          add_role: (addRole || undefined) as Role | undefined,
        },
      });
      toast.success("Staff updated");
      setFullName("");
      setEmail("");
      setPassword("");
      setAddRole("");
      onSaved();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setFullName("");
          setEmail("");
          setPassword("");
          setAddRole("");
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit staff</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs">Full name</Label>
            <Input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">New password (leave blank to keep)</Label>
            <Input
              type="text"
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1"
              placeholder="Optional"
            />
          </div>
          <div>
            <Label className="text-xs">Add role</Label>
            <Select
              value={addRole || undefined}
              onValueChange={(v) => setAddRole(v as Role)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="owner">Owner</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="host">Host</SelectItem>
                <SelectItem value="door">Door</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
